import {
  MUTATION_RESPONSE_RECEIVED,
  QUERY_RESPONSE_RECEIVED,
} from '@web-platform/shared-universal-constants/src/network';
import * as persistanceKeys from '@web-platform/shared-universal-constants/src/persistanceKeys';
import { ResultStatus } from '@web-platform/shared-universal-constants/src/radar';
import { SEARCH_PARAMS_CHANGED } from '@web-platform/shared-universal-constants/src/userSearch';
import {
  RADAR_CREATE_SESSIONS,
  RADAR_POLL,
} from '@web-platform/shared-universal-types/src/network/requestTypes';
import { isResponseContainFlightsBody } from '@web-platform/shared-universal-utils-frontend/src/helpers/radar/flightsResponse';
import {
  isRadarFlightsResponseError,
  getRadarFlightsResponseBody,
} from '@web-platform/shared-universal-utils-frontend/src/helpers/radar/radarFlightsResponse';
import { tryGetValue } from '@web-platform/shared-universal-utils-frontend/src/helpers/sessionStorage';

import { DURATION_FILTER_CHANGED, FILTERS_RESET } from '../../../../../../actions/filters';

import { mapDuration } from './durationMapper';

import type { Action } from '@web-platform/shared-universal-types/src/redux/banana/actions/actions-banana-types';
import type { DurationFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/duration';
import type { FiltersState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/state';

export const INITIAL_STATE = {
  min: 0,
  max: 0,
  value: null,
};

const filtersInSession = (): FiltersState =>
  // @ts-expect-error FiltersState type is not compatible with INITIAL_STATE
  tryGetValue(persistanceKeys.FILTERS, INITIAL_STATE);
const storedStateFilters = () => filtersInSession()?.duration ?? INITIAL_STATE;

const round = (value: number) => {
  const step = 30;

  if (value % step) {
    return Math.floor(value / step) * step + step;
  }

  return value;
};

export const createDurationReducer =
  (getInitialState: () => DurationFilterState = storedStateFilters) =>
  (state: DurationFilterState = getInitialState(), action: Action): DurationFilterState => {
    switch (action.type) {
      case MUTATION_RESPONSE_RECEIVED:
      case QUERY_RESPONSE_RECEIVED: {
        /** TODO: When done properly with multi-city this will have to be moved somewhere else.
         * We need the tripType to determine which property on the response should be taken
         * for min and max values. The stats object could be stored in the store separately and
         * read from a selector which return the filter, min, and max values.
         */
        switch (action.payload.type) {
          case RADAR_CREATE_SESSIONS:
          case RADAR_POLL: {
            if (
              isRadarFlightsResponseError(action.payload.response) ||
              !isResponseContainFlightsBody(action.payload.response)
            ) {
              return state;
            }

            const flightsResponseBody = getRadarFlightsResponseBody(action.payload.response);
            const stats = mapDuration(flightsResponseBody.filterStats);

            if (!stats) {
              return state;
            }

            const { itineraries } = stats;
            const min = round(itineraries.min_longest_itinerary_leg_duration || 0);
            const max = round(itineraries.max_longest_itinerary_leg_duration || 0);
            const isComplete =
              action.payload.response.body.context?.status === ResultStatus.COMPLETE;
            const currentValue =
              state.value !== null && isComplete ? Math.min(state.value, max) : state.value;

            return {
              min,
              max,
              ...(itineraries.min_duration !== undefined && {
                multiCityMin: round(itineraries.min_duration),
              }),
              ...(itineraries.max_duration !== undefined && {
                multiCityMax: round(itineraries.max_duration),
              }),
              value: currentValue,
            };
          }

          default: {
            return state;
          }
        }
      }

      case DURATION_FILTER_CHANGED:
        return { ...state, value: action.payload.value };

      case FILTERS_RESET:
        return { ...state, value: null };

      case SEARCH_PARAMS_CHANGED:
        return state;

      default:
        return state;
    }
  };

export default createDurationReducer();
export const get = (state: DurationFilterState): DurationFilterState => state;
